import { useState, useEffect } from 'react';
import { Pane, TextInputField, majorScale, Button, toaster, Select, FormField, Text, Heading } from 'evergreen-ui';
import { updateGuest, fetchGuestAttachments, uploadPei, uploadGuestAttachments, deleteGuestAttachment, deletePei } from '../services/guestsServices';
import { getExtraFieldsForGuest, getAllFieldNames, upsertExtraField } from '../services/guestExtraFieldsService';
import { ConfirmIcon, CrossIcon } from 'evergreen-ui';
import FileUploaderSingleUploadDemo from '../commons/FileUploaderSingleUploadDemo';
import FileUploaderMultipleUploadDemo from '../commons/FileUploaderMultipleUploadDemo';
import { ATTACHMENT_CATEGORIES } from '../constants/guestAttachments';
import { useRole } from '../contexts/RoleContext';

const EditGuestForm = ({ editedGuest, handleCancelEdit, fetchGuests }) => {
    const { effectiveIsDirector: isDirector } = useRole();
    const [localGuest, setLocalGuest] = useState({ ...editedGuest });
    const [guestAttachments, setGuestAttachments] = useState([]);
    const [peiVisible, setPeiVisible] = useState(Boolean(editedGuest?.revisione_pei_url));
    const [peiSelected, setPeiSelected] = useState(null); 
    const [attachmentsSelected, setAttachmentsSelected] = useState([]);
    const [attachmentCategory, setAttachmentCategory] = useState(ATTACHMENT_CATEGORIES[0]);
    const [attachmentsMarkedForDeletion, setAttachmentsMarkedForDeletion] = useState([]);
    const [peiMarkedForDeletion, setPeiMarkedForDeletion] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [allFieldNames, setAllFieldNames] = useState([]);
    const [extraFieldsState, setExtraFieldsState] = useState({});
    const [originalExtraFields, setOriginalExtraFields] = useState({});
    const [newFieldName, setNewFieldName] = useState('');
    const [newFieldValue, setNewFieldValue] = useState('');

    useEffect(() => {
        setLocalGuest((prev) => ({
            ...prev,
            id: editedGuest.id || prev.id,
        }));
    }, [editedGuest]);

    useEffect(() => {
        const loadAttachments = async () => {
            if (!editedGuest || !editedGuest.id) return;
            try {
                const atts = await fetchGuestAttachments(editedGuest.id);
                setGuestAttachments(atts || []);
                setPeiVisible(Boolean(editedGuest.revisione_pei_url));
            } catch (err) {
                console.error('Errore fetch attachments nel form:', err);
            }
        };
        loadAttachments();
    }, [editedGuest]);

    useEffect(() => {
        const loadExtraFields = async () => {
            if (!editedGuest?.id) return;
            try {
                const [guestFields, names] = await Promise.all([
                    getExtraFieldsForGuest(editedGuest.id),
                    getAllFieldNames(),
                ]);
                const state = {};
                (names || []).forEach(n => { state[n] = ''; });
                (guestFields || []).forEach(f => { state[f.field_name] = f.field_value || ''; });
                setAllFieldNames(names || []);
                setExtraFieldsState(state);
                setOriginalExtraFields({ ...state });
            } catch (err) {
                console.error('Errore caricamento campi aggiuntivi:', err);
            }
        };
        loadExtraFields();
    }, [editedGuest]);

    const handleInputChange = (field, value) => {
        setLocalGuest((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };

    const removePeiVisually = () => {
        setPeiMarkedForDeletion(true);
        setPeiVisible(false);
    };

    const handleAttachmentsChangeLocal = (files) => {
        setAttachmentsSelected(files || []);
    };

    const removeAttachmentVisually = (attachmentId) => {
        setAttachmentsMarkedForDeletion((prev) => (prev.includes(attachmentId) ? prev : [...prev, attachmentId]));
        setGuestAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    };

    const handleCancel = async () => {
        let atts = [];
        try {
            if (editedGuest && editedGuest.id) {
                atts = await fetchGuestAttachments(editedGuest.id);
            }
        } catch (err) {
            console.error('Errore reload attachments on cancel:', err);
        }
        setGuestAttachments(atts || []);
        setLocalGuest({ ...editedGuest });
        setAttachmentsMarkedForDeletion([]);
        setPeiMarkedForDeletion(false);
        setPeiSelected(null);
        setAttachmentsSelected([]);
        setAttachmentCategory(ATTACHMENT_CATEGORIES[0]);
        setPeiVisible(Boolean(editedGuest?.revisione_pei_url));
        setExtraFieldsState({ ...originalExtraFields });
        setNewFieldName('');
        setNewFieldValue('');
        handleCancelEdit();
    };

    const handleConfirmEdit = async () => {
        setProcessing(true);
        try {
            for (const attId of attachmentsMarkedForDeletion) {
                try {
                    await deleteGuestAttachment(attId);
                } catch (err) {
                    console.error('Errore rimozione allegato durante conferma:', err);
                }
            }

            if (peiMarkedForDeletion) {
                try {
                    await deletePei(editedGuest.id);
                } catch (err) {
                    console.error('Errore rimozione PEI durante conferma:', err);
                }
            }

            let newPeiUrl = localGuest.revisione_pei_url || null;
            if (peiSelected) {
                try {
                    const res = await uploadPei(editedGuest.id, peiSelected);
                    if (res && res.pei_url) {
                        newPeiUrl = res.pei_url;
                    }
                } catch (err) {
                    console.error('Errore upload PEI durante conferma:', err);
                }
            } else if (peiMarkedForDeletion) {
                newPeiUrl = null;
            }

            const payload = {
                nome: localGuest.nome,
                cognome: localGuest.cognome,
                data_nascita: localGuest.data_nascita,
                residenza: localGuest.residenza,
                domicilio: localGuest.domicilio,
                sesso: localGuest.sesso,
                luogo_nascita: localGuest.luogo_nascita,
                motivazione: localGuest.motivazione,
                revisione_pei_url: newPeiUrl,
                iban: localGuest.iban || null,
                telefono: localGuest.telefono || null,
                email: localGuest.email || null,
                avv_nome: localGuest.avv_nome || null,
                avv_cognome: localGuest.avv_cognome || null,
                avv_mail: localGuest.avv_mail || null,
                avv_telefono: localGuest.avv_telefono || null,
                avv_indirizzo: localGuest.avv_indirizzo || null,
                ass_nome: localGuest.ass_nome || null,
                ass_cognome: localGuest.ass_cognome || null,
                ass_mail: localGuest.ass_mail || null,
                ass_telefono: localGuest.ass_telefono || null,
                ass_indirizzo: localGuest.ass_indirizzo || null,
                spec_nome: localGuest.spec_nome || null,
                spec_cognome: localGuest.spec_cognome || null,
                spec_mail: localGuest.spec_mail || null,
                spec_telefono: localGuest.spec_telefono || null,
                spec_indirizzo: localGuest.spec_indirizzo || null,
                serd_nome: localGuest.serd_nome || null,
                serd_cognome: localGuest.serd_cognome || null,
                serd_mail: localGuest.serd_mail || null,
                serd_telefono: localGuest.serd_telefono || null,
                serd_indirizzo: localGuest.serd_indirizzo || null,
                madre_nome: localGuest.madre_nome || null,
                madre_cognome: localGuest.madre_cognome || null,
                madre_mail: localGuest.madre_mail || null,
                madre_telefono: localGuest.madre_telefono || null,
                madre_indirizzo: localGuest.madre_indirizzo || null,
                padre_nome: localGuest.padre_nome || null,
                padre_cognome: localGuest.padre_cognome || null,
                padre_mail: localGuest.padre_mail || null,
                padre_telefono: localGuest.padre_telefono || null,
                padre_indirizzo: localGuest.padre_indirizzo || null,
            };

            const resultUpdate = await updateGuest(editedGuest.id, payload);

            if (!resultUpdate) {
                toaster.danger("Errore durante l'aggiornamento dell'ospite.");
                setProcessing(false);
                return;
            }

            for (const [name, value] of Object.entries(extraFieldsState)) {
                const original = originalExtraFields[name] ?? '';
                if (value !== original) {
                    try {
                        await upsertExtraField(editedGuest.id, name, value);
                    } catch (err) {
                        console.error('Errore salvataggio campo aggiuntivo:', err);
                    }
                }
            }

            if (attachmentsSelected && attachmentsSelected.length > 0) {
                try {
                    await uploadGuestAttachments(editedGuest.id, attachmentsSelected, attachmentCategory);
                } catch (err) {
                    console.error('Errore upload allegati durante conferma:', err);
                }
            }

            let atts = [];
            try {
                atts = await fetchGuestAttachments(editedGuest.id);
                setGuestAttachments(atts || []);
            } catch (err) {
                console.error('Errore refresh attachments dopo conferma:', err);
            }

            await fetchGuests();

            toaster.success('Modifiche salvate con successo.');

            setPeiSelected(null);
            setAttachmentsSelected([]);
            setAttachmentCategory(ATTACHMENT_CATEGORIES[0]);
            setAttachmentsMarkedForDeletion([]);
            setPeiMarkedForDeletion(false);
            setNewFieldName('');
            setNewFieldValue('');

            handleCancelEdit();
        } catch (error) {
            console.error("Errore nella modifica dell'ospite:", error);
            toaster.danger("Errore nella modifica dell'ospite.");
        }
        setProcessing(false);
    };

    return (
        <Pane>
            <TextInputField
                label="Nome"
                value={localGuest.nome || ''}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Cognome"
                value={localGuest.cognome || ''}
                onChange={(e) => handleInputChange('cognome', e.target.value)}
                marginBottom={16}
            />
            <FormField label="Sesso" marginBottom={16}>
                <Select id="sex-select" value={localGuest.sesso || 'M'} onChange={(e) => handleInputChange('sesso', e.target.value)}>
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                </Select>
            </FormField>
            <TextInputField
                label="Data di nascita"
                type="date"
                value={localGuest.data_nascita}
                onChange={(e) => handleInputChange('data_nascita', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Luogo di nascita"
                value={localGuest.luogo_nascita}
                onChange={(e) => handleInputChange('luogo_nascita', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Residenza"
                value={localGuest.residenza}
                onChange={(e) => handleInputChange('residenza', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Domicilio"
                value={localGuest.domicilio}
                onChange={(e) => handleInputChange('domicilio', e.target.value)}
                marginBottom={16}
            />
            <FormField label="Motivazione" marginBottom={16}>
                <Select
                    id="motivation-select"
                    value={localGuest.motivazione || 'minori stranieri non accompagnati'}
                    onChange={(e) => handleInputChange('motivazione', e.target.value)}
                >
                    <option value="minori stranieri non accompagnati">Minori stranieri non accompagnati</option>
                    <option value="penale">Penale</option>
                    <option value="sociale">Sociale</option>
                    <option value="emergenza">Emergenza</option>
                </Select>
            </FormField>

            <Pane marginTop={majorScale(4)}>
                <Heading size={400} marginBottom={12}>Contatti ospite</Heading>
                <Pane display="flex" gap={16} flexWrap="wrap">
                    <TextInputField label="IBAN" value={localGuest.iban || ''} onChange={(e) => handleInputChange('iban', e.target.value)} flex={2} minWidth={200} />
                    <TextInputField label="Telefono" value={localGuest.telefono || ''} onChange={(e) => handleInputChange('telefono', e.target.value)} flex={1} minWidth={150} />
                    <TextInputField label="Email" type="email" value={localGuest.email || ''} onChange={(e) => handleInputChange('email', e.target.value)} flex={1} minWidth={200} />
                </Pane>
            </Pane>

            {[
                { label: 'Avvocato', prefix: 'avv' },
                { label: 'Assistente sociale', prefix: 'ass' },
                { label: 'Specialista di riferimento', prefix: 'spec' },
                { label: 'SerD (Servizio per le dipendenze)', prefix: 'serd' },
                { label: 'Madre', prefix: 'madre' },
                { label: 'Padre', prefix: 'padre' },
            ].map(({ label, prefix }) => (
                <Pane key={prefix} marginTop={majorScale(3)}>
                    <Heading size={400} marginBottom={12}>{label}</Heading>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Nome" value={localGuest[`${prefix}_nome`] || ''} onChange={(e) => handleInputChange(`${prefix}_nome`, e.target.value)} flex={1} minWidth={150} />
                        <TextInputField label="Cognome" value={localGuest[`${prefix}_cognome`] || ''} onChange={(e) => handleInputChange(`${prefix}_cognome`, e.target.value)} flex={1} minWidth={150} />
                        <TextInputField label="Telefono" value={localGuest[`${prefix}_telefono`] || ''} onChange={(e) => handleInputChange(`${prefix}_telefono`, e.target.value)} flex={1} minWidth={150} />
                        <TextInputField label="Email" type="email" value={localGuest[`${prefix}_mail`] || ''} onChange={(e) => handleInputChange(`${prefix}_mail`, e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Indirizzo del servizio" value={localGuest[`${prefix}_indirizzo`] || ''} onChange={(e) => handleInputChange(`${prefix}_indirizzo`, e.target.value)} flex={2} minWidth={250} />
                    </Pane>
                </Pane>
            ))}

            <Pane marginTop={majorScale(3)}>
                <Heading size={400} marginBottom={12}>Campi personalizzati</Heading>
                {allFieldNames.map(name => (
                    <TextInputField
                        key={name}
                        label={name}
                        value={extraFieldsState[name] || ''}
                        onChange={(e) => setExtraFieldsState(prev => ({ ...prev, [name]: e.target.value }))}
                        marginBottom={12}
                    />
                ))}
                {isDirector && (
                <Pane display="flex" gap={8} alignItems="flex-end" flexWrap="wrap" marginTop={4}>
                    <TextInputField
                        label="Nome nuovo campo"
                        placeholder="es. Telefono nonna"
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                        flex={1}
                        minWidth={180}
                        marginBottom={0}
                    />
                    <TextInputField
                        label="Valore"
                        value={newFieldValue}
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        flex={1}
                        minWidth={180}
                        marginBottom={0}
                    />
                    <Button
                        marginBottom={16}
                        disabled={!newFieldName.trim()}
                        onClick={() => {
                            const trimmed = newFieldName.trim();
                            if (!trimmed) return;
                            setExtraFieldsState(prev => ({ ...prev, [trimmed]: newFieldValue.trim() }));
                            if (!allFieldNames.includes(trimmed)) {
                                setAllFieldNames(prev => [...prev, trimmed]);
                            }
                            setNewFieldName('');
                            setNewFieldValue('');
                        }}
                    >
                        + Aggiungi campo
                    </Button>
                </Pane>
                )}
            </Pane>

            <Pane marginTop={majorScale(4)}>
                <Text fontWeight={600}>PEI</Text>
                <Pane marginTop={8}>
                    {peiVisible && editedGuest.revisione_pei_url ? (
                        <Pane display="flex" alignItems="center" gap={12}>
                            <Text cursor="pointer" onClick={() => window.open(editedGuest.revisione_pei_url, '_blank', 'noopener,noreferrer')}>
                                {editedGuest.revisione_pei_url.split('/').pop()}
                            </Text>
                            <Button appearance="primary" intent="danger" onClick={removePeiVisually}>
                                Rimuovi
                            </Button>
                        </Pane>
                    ) : (
                        <Text>Nessun PEI caricato.</Text>
                    )}

                    <Pane marginTop={8} display="flex" alignItems="center" gap={8}>
                        <FileUploaderSingleUploadDemo onFileChange={(file) => setPeiSelected(file)} />
                    </Pane>
                </Pane>

                <Text fontWeight={600} marginTop={16}>Allegati</Text>
                <Pane marginTop={8}>
                    {guestAttachments.length > 0 ? (
                        ATTACHMENT_CATEGORIES.map((cat) => {
                            const items = guestAttachments.filter(a => a.category === cat);
                            if (items.length === 0) return null;
                            return (
                                <Pane key={cat} marginBottom={10}>
                                    <Text size={300} fontWeight={600} textTransform="capitalize">{cat}</Text>
                                    <Pane display="flex" flexDirection="column" gap={6} marginTop={4}>
                                        {items.map((att) => (
                                            <Pane key={att.id} display="flex" alignItems="center" gap={8}>
                                                <Text cursor="pointer" onClick={() => window.open(att.file_url, '_blank', 'noopener,noreferrer')}>
                                                    {att.file_name}
                                                </Text>
                                                <Button appearance="primary" intent="danger" onClick={() => removeAttachmentVisually(att.id)}>
                                                    Rimuovi
                                                </Button>
                                            </Pane>
                                        ))}
                                    </Pane>
                                </Pane>
                            );
                        })
                    ) : (
                        <Text>Nessun allegato.</Text>
                    )}
                    {guestAttachments.filter(a => !a.category).length > 0 && (
                        <Pane marginBottom={10}>
                            <Text size={300} fontWeight={600}>Senza categoria</Text>
                            <Pane display="flex" flexDirection="column" gap={6} marginTop={4}>
                                {guestAttachments.filter(a => !a.category).map((att) => (
                                    <Pane key={att.id} display="flex" alignItems="center" gap={8}>
                                        <Text cursor="pointer" onClick={() => window.open(att.file_url, '_blank', 'noopener,noreferrer')}>
                                            {att.file_name}
                                        </Text>
                                        <Button appearance="primary" intent="danger" onClick={() => removeAttachmentVisually(att.id)}>
                                            Rimuovi
                                        </Button>
                                    </Pane>
                                ))}
                            </Pane>
                        </Pane>
                    )}

                    <Pane marginTop={12} display="flex" flexDirection="column" gap={8}>
                        <FormField label="Cartella allegati">
                            <Select value={attachmentCategory} onChange={(e) => setAttachmentCategory(e.target.value)}>
                                {ATTACHMENT_CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Select>
                        </FormField>
                        <FileUploaderMultipleUploadDemo onFilesChange={handleAttachmentsChangeLocal} />
                    </Pane>
                </Pane>
            </Pane>

            <Pane display="flex" justifyContent="space-between" marginTop={majorScale(4)}>
                <Button appearance="primary" intent="success" onClick={handleConfirmEdit} disabled={processing}>
                    {processing ? 'Salvo...' : 'Conferma modifica'} <ConfirmIcon marginLeft={8} />
                </Button>
                <Button intent="danger" onClick={handleCancel} disabled={processing}>
                    Annulla <CrossIcon marginLeft={8} />
                </Button>
            </Pane>
        </Pane>
    );
};

export default EditGuestForm;